import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../config/api';
import { ChatMessage, ChatMessageRequest } from '../types/chat';

interface UseChatMessagesOptions {
  bookId: string;
  sessionId: string | null;
}

export const useChatMessages = ({ bookId, sessionId }: UseChatMessagesOptions) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const oldestMessageIdRef = useRef<string | null>(null);

  // Per-session message cache for background streaming
  const [messagesCache, setMessagesCache] = useState<Record<string, ChatMessage[]>>({});
  const messagesCacheRef = useRef<Record<string, ChatMessage[]>>({});

  // Track previous sessionId for saving to cache on switch
  const prevSessionIdRef = useRef<string | null>(null);

  // Track current sessionId for real-time access
  const sessionIdRef = useRef<string | null>(null);

  // Ref to always have latest messages (avoids stale closure in session change effect)
  const messagesRef = useRef<ChatMessage[]>([]);

  // Keep refs in sync with state
  useEffect(() => {
    messagesCacheRef.current = messagesCache;
  }, [messagesCache]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Fetch messages for the session
  const fetchMessages = useCallback(async (beforeMessageId?: string) => {
    if (!sessionId) return;

    const isLoadingMore = !!beforeMessageId;

    if (isLoadingMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      const params: any = { limit: 50 };
      if (beforeMessageId) {
        params.before_message_id = beforeMessageId;
      }

      const response = await api.get<ChatMessage[]>(
        `/api/chat/sessions/${sessionId}/messages`,
        { params }
      );

      const newMessages = response.data;

      if (isLoadingMore) {
        // Prepend older messages
        setMessages(prev => [...newMessages, ...prev]);
      } else {
        // Replace all messages (initial load or refresh)
        setMessages(newMessages);
      }

      // Update oldest message ID for pagination
      if (newMessages.length > 0) {
        oldestMessageIdRef.current = newMessages[0].id;
        setHasMore(newMessages.length === 50); // If we got less than limit, no more messages
      } else {
        setHasMore(false);
      }
    } catch (err) {
      setError('Failed to fetch messages');
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sessionId]);

  // Load more messages (pagination)
  const loadMoreMessages = useCallback(async () => {
    if (!hasMore || loadingMore || !oldestMessageIdRef.current) return;
    
    await fetchMessages(oldestMessageIdRef.current);
  }, [fetchMessages, hasMore, loadingMore]);

  // Send a new message
  const sendMessage = useCallback(async (
    messageData: ChatMessageRequest,
    options?: { skipOptimistic?: boolean }
  ) => {
    if (!sessionId) return null;

    let tempId: string | undefined;

    // Only add optimistic message if not skipped
    if (!options?.skipOptimistic) {
      // Create optimistic message with temporary ID
      tempId = `temp-${Date.now()}`; // Temporary string ID
      const optimisticMessage: ChatMessage = {
        id: tempId,
        session_id: sessionId,
        message_type: messageData.message_type || 'user',
        content_text: messageData.content_text,
        page_number: messageData.page_number,
        attachments: messageData.attachments,
        created_at: new Date().toISOString()
      };

      // Add optimistic message immediately
      setMessages(prev => [...prev, optimisticMessage]);
    }

    try {
      const response = await api.post<ChatMessage>(
        `/api/chat/sessions/${sessionId}/messages`,
        messageData
      );

      // Only replace if we added an optimistic message
      if (tempId !== undefined) {
        setMessages(prev =>
          prev.map(msg => msg.id === tempId ? response.data : msg)
        );
      }

      return response.data;
    } catch (err) {
      console.error('Error sending message:', err);
      // Remove optimistic message on error (only if we added one)
      if (tempId !== undefined) {
        setMessages(prev => prev.filter(msg => msg.id !== tempId));
      }
      throw err;
    }
  }, [sessionId]);

  // Add a message locally (for optimistic updates or system messages)
  const addLocalMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => [...prev, message]);
  }, []);

  // Clear messages (when switching sessions)
  const clearMessages = useCallback(() => {
    setMessages([]);
    oldestMessageIdRef.current = null;
    setHasMore(true);
    setError(null);
  }, []);

  // Handle session changes with caching
  useEffect(() => {
    if (sessionId) {
      // Save current messages to cache before switching (if we have previous session)
      // Use messagesRef.current to get the LATEST messages value (avoids stale closure)
      const currentMessages = messagesRef.current;
      if (prevSessionIdRef.current && prevSessionIdRef.current !== sessionId && currentMessages.length > 0) {
        const newCache = { ...messagesCacheRef.current, [prevSessionIdRef.current]: currentMessages };
        messagesCacheRef.current = newCache;
        setMessagesCache(newCache);
      }

      // Check cache for new session (use ref for real-time value)
      if (messagesCacheRef.current[sessionId]) {
        setMessages(messagesCacheRef.current[sessionId]);
        oldestMessageIdRef.current = messagesCacheRef.current[sessionId][0]?.id || null;
      } else {
        // Not in cache - fetch from backend
        clearMessages();
        fetchMessages();
      }

      // Update previous session ref
      prevSessionIdRef.current = sessionId;
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to update cache for a specific session (for background streaming)
  const updateCacheForSession = useCallback((targetSessionId: string, updater: (prev: ChatMessage[]) => ChatMessage[]) => {
    setMessagesCache(prev => {
      const newCache = {
        ...prev,
        [targetSessionId]: updater(prev[targetSessionId] || [])
      };
      // Also update ref immediately for real-time access
      messagesCacheRef.current = newCache;
      return newCache;
    });
  }, []);

  // Function to add a message to cache for a specific session
  const addMessageToCache = useCallback((targetSessionId: string, message: ChatMessage) => {
    setMessagesCache(prev => {
      const newCache = {
        ...prev,
        [targetSessionId]: [...(prev[targetSessionId] || []), message]
      };
      messagesCacheRef.current = newCache;
      return newCache;
    });
  }, []);

  return {
    messages,
    loading,
    loadingMore,
    error,
    hasMore,
    sendMessage,
    loadMoreMessages,
    refreshMessages: () => fetchMessages(),
    addLocalMessage,
    clearMessages,
    setMessages,
    // New functions for background streaming support
    updateCacheForSession,
    addMessageToCache,
    sessionIdRef,
    messagesCacheRef
  };
};